/**
 * Goods-receipt detail — document-style view of a posted GRN.
 *
 * Faithful to `prototype/purchases/goods-receipt-detail.html`:
 *   - composer-head pattern : back arrow + "Goods receipt" + GRN number pill +
 *                             status pill; sub-line vendor · against PO ·
 *                             received date. Head actions: View purchase order +
 *                             (when present) View bill.
 *   - info ribbon           : shortage / clean-receipt callout summarising
 *                             received vs accepted.
 *   - doc-stage             : the GRN document (letterhead → supplier + logistics
 *                             grid → per-line Ordered/Received/Accepted/Rejected/
 *                             Reason table → totals → inspection notes) + a sticky
 *                             action rail (next action · receipt summary · supplier).
 *
 * Quantities render from the persisted `*Milli` fields; the draft matching
 * amount is `acceptedValuePaise`. A GRN is append-only — a posted receipt is
 * never edited, only **voided** (which reverses PO fulfilment + voids the draft
 * bill). A *draft* receipt offers Edit / Post / Delete from the action rail.
 *
 * @see prototype/purchases/goods-receipt-detail.html — V1.1 baseline
 * @see packages/api-contract/src/procedures/goods-receipts.ts
 */
import { useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { hasPermission } from "@flint/api-contract";
import { meQueryOptions } from "../../../lib/session.js";
import {
  ArrowRight,
  Check,
  CircleCheck,
  ExternalLink,
  Pencil,
  Printer,
  ShoppingCart,
  Trash2,
  TriangleAlert,
  Undo2,
} from "lucide-react";
import { Alert, Button, ConfirmDialog, StatusPill, toast } from "@flint/ui";
import { trpc } from "../../../lib/trpc.js";
import { formatDate, paiseToInr } from "../../../lib/format.js";
import { openDocPdf } from "../../../lib/pdf.js";
import { grnStatusLabel, grnStatusTone, type GrnStatus } from "../lib/status.js";
import { GrnVoidDialog } from "./grn-void-dialog.js";

interface GrnDetailPageProps {
  grnId: string;
}

function describeLoadError(code: string | undefined): string {
  switch (code) {
    case "PARSE_ERROR":
    case "BAD_REQUEST":
      return "This goods receipt link isn't valid.";
    case "NOT_FOUND":
      return "Goods receipt not found.";
    case "UNAUTHORIZED":
    case "FORBIDDEN":
      return "You don't have access to this goods receipt.";
    default:
      return "Couldn't load this goods receipt. Try again in a moment.";
  }
}

/**
 * Swap the raw domain error text (which embeds the internal GRN UUID) for
 * plain-English, actionable copy. Prefers the structured `data.cause.subReason`
 * when present; falls back to matching stable phrases from the domain error
 * messages (see `packages/domain/src/purchases/grns/errors.ts`).
 */
function describeVoidError(err: {
  data?: { code?: string | undefined; cause?: { subReason?: string | undefined } | undefined } | undefined | null;
  message?: string | undefined | null;
}): string {
  const sub = err.data?.cause?.subReason;
  const msg = err.message ?? "";
  if (sub === "bill-not-voidable" || /matching bill is .* can't be reversed/i.test(msg)) {
    return "This receipt's matching bill is already paid — reverse the payment on the bill before voiding the receipt.";
  }
  if (sub === "not-voidable" || /and cannot be voided/i.test(msg)) {
    return "Only posted goods receipts can be voided. Refresh and try again.";
  }
  switch (err.data?.code) {
    case "NOT_FOUND":
      return "Goods receipt no longer exists.";
    case "UNAUTHORIZED":
    case "FORBIDDEN":
      return "You don't have access to void this goods receipt.";
    default:
      return "Couldn't void this goods receipt. Try again in a moment.";
  }
}

export function GrnDetailPage({ grnId }: GrnDetailPageProps) {
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const me = useQuery(meQueryOptions);
  const canManage = hasPermission(me.data?.company?.permissions ?? [], "goodsReceipts.manage");
  const [voidOpen, setVoidOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const query = trpc.goodsReceipts.get.useQuery({ id: grnId });

  const invalidate = async () => {
    await Promise.all([
      utils.goodsReceipts.get.invalidate({ id: grnId }),
      utils.goodsReceipts.list.invalidate(),
      utils.purchaseOrders.list.invalidate(),
    ]);
  };

  const post = trpc.goodsReceipts.post.useMutation({
    onSuccess: async (res: { grnNumber: string; billId: string | null }) => {
      await invalidate();
      toast.success(`Goods receipt ${res.grnNumber} posted`, {
        description: res.billId
          ? "PO fulfilment updated and a draft matching bill was created."
          : "PO fulfilment updated.",
      });
    },
    onError: (err: { message: string }) =>
      toast.error("Couldn't post goods receipt", { description: err.message }),
  });

  const deleteDraft = trpc.goodsReceipts.deleteDraft.useMutation({
    onSuccess: async () => {
      await Promise.all([
        utils.goodsReceipts.list.invalidate(),
        utils.purchaseOrders.list.invalidate(),
      ]);
      setDeleteOpen(false);
      toast.success("Draft deleted", {
        description: "The draft goods receipt has been removed.",
      });
      void navigate({ to: "/procurement/goods-receipts" });
    },
    onError: (err: { message: string }) =>
      toast.error("Couldn't delete draft", { description: err.message }),
  });

  const voidGrn = trpc.goodsReceipts.void.useMutation({
    onSuccess: async () => {
      await invalidate();
      setVoidOpen(false);
      toast.success("Goods receipt voided", {
        description: "PO fulfilment was recalculated and the matching bill voided.",
      });
    },
    onError: (err) =>
      toast.error("Couldn't void goods receipt", { description: describeVoidError(err) }),
  });

  const busy = post.isPending || deleteDraft.isPending || voidGrn.isPending;

  if (query.isLoading) {
    return <p className="p-6 text-sm text-muted">Loading goods receipt…</p>;
  }
  if (query.error || !query.data) {
    return (
      <div className="p-6">
        <Alert
          intent="bad"
          title="Couldn't load goods receipt"
          description={describeLoadError(query.error?.data?.code)}
        />
      </div>
    );
  }

  const grn = query.data;
  const status = grn.status as GrnStatus;
  const vendorLocation = [grn.vendorCity, grn.vendorState].filter(Boolean).join(", ");

  const orderedMilli = grn.lines.reduce((a, l) => a + l.orderedQtyMilli, 0);
  const receivedMilli = grn.lines.reduce((a, l) => a + l.receivedQtyMilli, 0);
  const acceptedMilli = grn.lines.reduce((a, l) => a + l.acceptedQtyMilli, 0);
  const rejectedMilli = grn.lines.reduce((a, l) => a + l.rejectedQtyMilli, 0);
  const hasShortage = grn.rejectedLineCount > 0 || receivedMilli < orderedMilli;

  return (
    <div className="flex min-h-full flex-col bg-bg">
      {/* Head — composer-head pattern. */}
      <header className="sticky top-0 z-20 flex h-14 items-center gap-3.5 border-b border-line bg-paper px-6">
        <Link
          to="/procurement/goods-receipts"
          aria-label="Back to goods receipts"
          className="grid h-9 w-9 shrink-0 place-items-center rounded-sm border border-line text-ink-2 transition hover:bg-bg"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            aria-hidden="true"
          >
            <path d="m15 18-6-6 6-6" />
          </svg>
        </Link>
        <div className="min-w-0 flex-1">
          <h1 className="flex items-center gap-2 text-base font-bold leading-tight tracking-tight text-ink">
            Goods receipt
            <span className="rounded-full bg-raised px-2 py-0.5 font-mono text-xs font-medium text-muted">
              {grn.grnNumber ?? "Draft"}
            </span>
            <StatusPill tone={grnStatusTone(status)} size="sm" dot>
              {hasShortage && status === "posted" ? "Posted · shortage" : grnStatusLabel(status)}
            </StatusPill>
          </h1>
          {/* Sub-line hidden on narrow screens — the vendor / PO / date
              context lives in the doc body just below, and packing all four
              tokens into a 56px sticky bar clashes with the action buttons at
              mobile widths (AC128). */}
          <p className="mt-0.5 hidden truncate text-xs text-muted sm:block">
            <strong className="text-ink-2">{grn.vendorName}</strong> · Against{" "}
            <span className="font-mono">{grn.poNumber ?? "—"}</span> · Received{" "}
            {formatDate(grn.receivedDate)}
            {hasShortage ? " · shortage recorded" : ""}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <button
            type="button"
            onClick={() => openDocPdf(`/api/goods-receipts/${grnId}/pdf`)}
            className="inline-flex h-9 items-center gap-1.5 rounded-sm border border-line bg-paper px-3 text-xs font-semibold text-ink transition hover:bg-bg"
          >
            <Printer size={13} />
            <span className="hidden sm:inline">Print</span>
          </button>
          <Link
            to="/procurement/purchase-orders/$poId"
            params={{ poId: grn.purchaseOrderId }}
            aria-label="View purchase order"
            className="inline-flex h-9 items-center gap-1.5 rounded-sm border border-line bg-paper px-3 text-xs font-semibold text-ink-2 transition hover:bg-bg"
          >
            <ExternalLink size={13} />
            <span className="hidden sm:inline">View purchase order</span>
          </Link>
          {grn.billId ? (
            <Link
              to="/procurement/bills/$billId"
              params={{ billId: grn.billId }}
              aria-label="View bill"
              className="inline-flex h-9 items-center gap-1.5 rounded-sm border border-line bg-paper px-3 text-xs font-semibold text-ink-2 transition hover:bg-bg"
            >
              <ShoppingCart size={13} />
              <span className="hidden sm:inline">View bill</span>
            </Link>
          ) : null}
        </div>
      </header>

      <main className="mx-auto grid w-full max-w-[1200px] flex-1 gap-5 px-6 pb-16 pt-5 lg:grid-cols-[minmax(0,1fr)_300px]">
        <div className="flex min-w-0 flex-col gap-4">
          {/* Info ribbon */}
          <div
            className={[
              "flex items-start gap-3 rounded-md border px-4 py-3",
              hasShortage
                ? "border-amber-100 border-l-[3px] border-l-amber-500 bg-warn-50"
                : "border-sky-100 border-l-[3px] border-l-sky-600 bg-sky-50",
            ].join(" ")}
            role="status"
          >
            <span
              className={`mt-0.5 grid h-5 w-5 shrink-0 place-items-center ${hasShortage ? "text-amber-600" : "text-sky-700"}`}
            >
              {hasShortage ? (
                <TriangleAlert size={16} strokeWidth={2.4} />
              ) : (
                <CircleCheck size={16} strokeWidth={2.4} />
              )}
            </span>
            <div className="min-w-0 flex-1 text-xs leading-relaxed text-ink-2">
              <strong className="text-ink">
                {grn.grnNumber ?? "This receipt"} {status === "posted" ? "posted" : status}
                {hasShortage ? " with shortage." : "."}
              </strong>{" "}
              Received <span className="font-mono">{formatMilli(receivedMilli)}</span> of{" "}
              <span className="font-mono">{formatMilli(orderedMilli)}</span> ordered units. Accepted{" "}
              <span className="font-mono">{formatMilli(acceptedMilli)}</span>
              {rejectedMilli > 0 ? (
                <>
                  ; <span className="font-mono">{formatMilli(rejectedMilli)}</span> units short or
                  rejected.
                </>
              ) : (
                "."
              )}
              {grn.billId ? (
                <div className="mt-1.5 inline-flex items-center gap-1.5 text-emerald-700">
                  <CircleCheck size={12} strokeWidth={2.6} />
                  Draft matching bill auto-created for the accepted quantity.
                </div>
              ) : null}
            </div>
            {grn.billId ? (
              <Link
                to="/procurement/bills/$billId"
                params={{ billId: grn.billId }}
                className="shrink-0 text-xs font-semibold text-sky-700 hover:underline"
              >
                View bill →
              </Link>
            ) : null}
          </div>

          {/* Document */}
          <article className="overflow-hidden rounded-md border border-line bg-paper shadow-sm">
            <div className="border-b border-line bg-raised px-5 py-2.5 text-xs text-muted">
              <strong className="text-ink">{grn.grnNumber ?? "Draft GRN"}</strong> ·{" "}
              {grn.vendorName} · {formatDate(grn.receivedDate)}
            </div>
            <div className="p-6">
              {/* Letterhead marker */}
              <div className="flex flex-wrap items-start justify-between gap-4 border-b border-line pb-5">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted">
                    Goods receipt note
                  </p>
                  <h2 className="mt-1 font-mono text-xl font-bold text-ink">
                    {grn.grnNumber ?? "Draft"}
                  </h2>
                </div>
                <dl className="text-right text-sm">
                  <div className="flex justify-end gap-2">
                    <dt className="text-muted">Against PO</dt>
                    <dd className="font-mono text-ink-2">{grn.poNumber ?? "—"}</dd>
                  </div>
                  <div className="mt-1 flex justify-end gap-2">
                    <dt className="text-muted">Received</dt>
                    <dd className="font-mono text-ink-2">{formatDate(grn.receivedDate)}</dd>
                  </div>
                  <div className="mt-1 flex justify-end gap-2">
                    <dt className="text-muted">Status</dt>
                    <dd className="text-ink-2">
                      {hasShortage && status === "posted" ? "Posted · shortage" : grnStatusLabel(status)}
                    </dd>
                  </div>
                </dl>
              </div>

              {/* Supplier + logistics grid */}
              <div className="mt-5 grid gap-5 sm:grid-cols-2">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted">Supplier</p>
                  <p className="mt-1 text-base font-bold text-ink">{grn.vendorName}</p>
                  {vendorLocation ? <p className="text-sm text-muted">{vendorLocation}</p> : null}
                  {grn.vendorGstin ? (
                    <p className="mt-1 font-mono text-xs text-muted">GSTIN {grn.vendorGstin}</p>
                  ) : null}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <LogisticsCell label="Vehicle / challan" value={grn.vehicleChallan || "—"} />
                  <LogisticsCell label="Received by" value={grn.receivedBy || "—"} />
                  <LogisticsCell label="Ordered (units)" value={formatMilli(orderedMilli)} />
                  <LogisticsCell label="Accepted (units)" value={formatMilli(acceptedMilli)} />
                </div>
              </div>

              {/* Per-line receipt table */}
              <table className="mt-5 w-full border-collapse text-sm">
                <thead>
                  <tr className="border-y border-line text-xs uppercase tracking-wide text-muted">
                    <th className="py-2 text-left font-medium">Item</th>
                    <th className="py-2 text-right font-medium">Ordered</th>
                    <th className="py-2 text-right font-medium">Received</th>
                    <th className="py-2 text-right font-medium">Accepted</th>
                    <th className="py-2 text-right font-medium">Rejected</th>
                    <th className="py-2 text-left font-medium">Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {grn.lines.map((line) => {
                    const rejected = line.rejectedQtyMilli > 0;
                    return (
                      <tr key={line.id} className="border-b border-line">
                        <td className="py-2.5 text-ink">
                          {line.description}
                          {line.hsn ? (
                            <span className="mt-0.5 block font-mono text-xs text-muted">
                              HSN {line.hsn}
                            </span>
                          ) : null}
                        </td>
                        <td className="py-2.5 text-right font-mono text-ink-2">
                          {formatMilli(line.orderedQtyMilli)}
                        </td>
                        <td className="py-2.5 text-right font-mono text-ink-2">
                          {formatMilli(line.receivedQtyMilli)}
                        </td>
                        <td className="py-2.5 text-right font-mono text-ink-2">
                          {formatMilli(line.acceptedQtyMilli)}
                        </td>
                        <td
                          className={`py-2.5 text-right font-mono ${rejected ? "font-semibold text-rose-600" : "text-muted"}`}
                        >
                          {formatMilli(line.rejectedQtyMilli)}
                        </td>
                        <td className="py-2.5 text-xs text-muted">
                          {line.rejectReason || "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {/* Totals */}
              <div className="mt-5 flex flex-col items-end gap-1.5 text-sm">
                <div className="flex w-full max-w-xs justify-between">
                  <span className="text-muted">Total ordered</span>
                  <span className="font-mono text-ink-2">{formatMilli(orderedMilli)} units</span>
                </div>
                <div className="flex w-full max-w-xs justify-between">
                  <span className="text-muted">Total received</span>
                  <span className="font-mono text-ink-2">{formatMilli(receivedMilli)} units</span>
                </div>
                <div className="flex w-full max-w-xs justify-between">
                  <span className="text-muted">Total accepted</span>
                  <span className="font-mono text-ink-2">{formatMilli(acceptedMilli)} units</span>
                </div>
                <div className="flex w-full max-w-xs justify-between border-t border-line pt-1.5">
                  <span className="font-semibold text-ink">Short / rejected</span>
                  <span
                    className={`font-mono text-base font-bold ${rejectedMilli > 0 ? "text-rose-600" : "text-ink"}`}
                  >
                    {formatMilli(rejectedMilli)} units
                  </span>
                </div>
                <div className="flex w-full max-w-xs justify-between">
                  <span className="text-muted">Draft matching amount</span>
                  <span className="font-mono text-ink-2">{paiseToInr(grn.acceptedValuePaise)}</span>
                </div>
              </div>

              {grn.notes ? (
                <div className="mt-5 rounded-md border border-line bg-raised p-3 text-sm text-ink-2">
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted">
                    Inspection notes
                  </p>
                  <p className="mt-1 whitespace-pre-line">{grn.notes}</p>
                </div>
              ) : null}
            </div>
          </article>
        </div>

        {/* Action rail */}
        <aside className="w-full">
          <div className="sticky top-20 space-y-4">
            <div className="rounded-md border border-line bg-paper p-3.5 shadow-sm">
              <h4 className="text-sm font-semibold text-ink">
                {status === "draft" ? "Draft — not posted" : "Next action"}
              </h4>
              <p className="mt-2 text-xs leading-relaxed text-muted">
                {status === "draft"
                  ? "Saved as a draft — nothing has reached the PO or the bills ledger yet. Post it to flip the purchase order and draft a matching bill."
                  : status === "void"
                    ? "This goods receipt is void. Its PO fulfilment was recalculated and any matching bill voided. The row stays as a historical record."
                    : hasShortage
                      ? "A shortfall was recorded — open a balance receipt when the follow-up consignment arrives, or review the matching bill."
                      : "Goods accepted in full. Review the matching bill when you're ready to pay."}
              </p>
              <div className="mt-3 flex flex-col gap-2">
                {status === "draft" && canManage ? (
                  <>
                    <Button
                      disabled={busy}
                      className="justify-start"
                      onClick={() => post.mutate({ id: grnId })}
                    >
                      <Check size={15} />
                      Post goods receipt
                    </Button>
                    <Button
                      asChild
                      intent="ghost"
                      className="justify-start border border-line bg-paper"
                    >
                      <Link to="/procurement/goods-receipts/$grnId/edit" params={{ grnId }}>
                        <Pencil size={15} />
                        Edit draft
                      </Link>
                    </Button>
                  </>
                ) : null}

                <Link
                  to="/procurement/purchase-orders/$poId"
                  params={{ poId: grn.purchaseOrderId }}
                  className="inline-flex h-9 items-center justify-start gap-2 rounded-sm border border-line bg-paper px-3 text-sm font-semibold text-ink-2 transition hover:bg-bg"
                >
                  <ExternalLink size={15} />
                  View purchase order
                </Link>
                {grn.billId ? (
                  <Link
                    to="/procurement/bills/$billId"
                    params={{ billId: grn.billId }}
                    className="inline-flex h-9 items-center justify-start gap-2 rounded-sm bg-ink px-3 text-sm font-semibold text-white transition hover:bg-ink-2"
                  >
                    <ShoppingCart size={15} />
                    View bill
                  </Link>
                ) : null}

                {status === "draft" && canManage ? (
                  <Button
                    intent="ghost"
                    disabled={busy}
                    className="justify-start text-bad hover:bg-bad-50"
                    onClick={() => setDeleteOpen(true)}
                  >
                    <Trash2 size={15} />
                    Delete draft
                  </Button>
                ) : null}
                {status === "posted" && canManage ? (
                  <Button
                    intent="ghost"
                    disabled={busy}
                    className="justify-start text-bad hover:bg-bad-50"
                    onClick={() => setVoidOpen(true)}
                  >
                    <Undo2 size={15} />
                    Void receipt
                  </Button>
                ) : null}
              </div>
            </div>

            <div className="rounded-md border border-line bg-paper p-3.5 shadow-sm">
              <h4 className="text-xs font-semibold uppercase tracking-wider text-muted">
                Receipt summary
              </h4>
              <dl className="mt-3 space-y-2 text-sm">
                <SummaryRow label="Ordered" value={`${formatMilli(orderedMilli)} units`} />
                <SummaryRow label="Received" value={`${formatMilli(receivedMilli)} units`} />
                <SummaryRow label="Accepted" value={`${formatMilli(acceptedMilli)} units`} />
                <div className="flex justify-between border-t border-line pt-2">
                  <dt className="font-semibold text-ink">Short / rejected</dt>
                  <dd
                    className={`font-mono font-semibold ${rejectedMilli > 0 ? "text-rose-600" : "text-ink"}`}
                  >
                    {formatMilli(rejectedMilli)} units
                  </dd>
                </div>
                <SummaryRow label="Matching amount" value={paiseToInr(grn.acceptedValuePaise)} mono />
              </dl>
            </div>

            <div className="rounded-md border border-line bg-paper p-3.5 shadow-sm">
              <div className="flex items-center justify-between">
                <h4 className="text-xs font-semibold uppercase tracking-wider text-muted">Supplier</h4>
                <Link
                  to="/procurement/vendors/$vendorId"
                  params={{ vendorId: grn.vendorId }}
                  className="inline-flex items-center gap-1 text-xs font-semibold text-sky-700 hover:underline"
                >
                  Open <ArrowRight size={11} />
                </Link>
              </div>
              <div className="mt-3 flex items-center gap-2.5">
                <div className="grid h-9 w-9 shrink-0 place-items-center rounded-sm bg-violet-600 text-xs font-bold text-white">
                  {initials(grn.vendorName)}
                </div>
                <div className="min-w-0">
                  <div className="truncate text-sm font-semibold text-ink">{grn.vendorName}</div>
                  {grn.vendorGstin ? (
                    <div className="truncate font-mono text-xs text-muted">{grn.vendorGstin}</div>
                  ) : null}
                </div>
              </div>
              <dl className="mt-3 space-y-2 text-sm">
                <SummaryRow label="PO ref" value={grn.poNumber ?? "—"} mono />
                {grn.billId ? (
                  <div className="flex justify-between">
                    <dt className="text-muted">Draft bill</dt>
                    <dd>
                      <Link
                        to="/procurement/bills/$billId"
                        params={{ billId: grn.billId }}
                        className="font-mono text-sky-700 hover:underline"
                      >
                        View →
                      </Link>
                    </dd>
                  </div>
                ) : null}
              </dl>
            </div>
          </div>
        </aside>
      </main>

      <GrnVoidDialog
        open={voidOpen}
        onOpenChange={setVoidOpen}
        grnNumber={grn.grnNumber}
        hasBill={grn.billId !== null}
        pending={voidGrn.isPending}
        {...(voidGrn.error ? { errorMessage: describeVoidError(voidGrn.error) } : {})}
        onConfirm={({ reason }) => voidGrn.mutate({ id: grnId, ...(reason ? { reason } : {}) })}
      />

      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title="Delete this draft?"
        description={`The draft goods receipt against ${grn.poNumber ?? "this PO"} and its lines will be removed. Drafts never reach the PO or the ledger — there's nothing to reverse.`}
        intent="danger"
        confirmLabel={deleteDraft.isPending ? "Deleting…" : "Delete draft"}
        onConfirm={() => deleteDraft.mutate({ id: grnId })}
      />
    </div>
  );
}

function LogisticsCell({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <div className="text-xs font-semibold uppercase tracking-[0.06em] text-muted">{label}</div>
      <div className="mt-0.5 truncate font-mono text-sm text-ink-2">{value}</div>
    </div>
  );
}

function SummaryRow({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex justify-between">
      <dt className="text-muted">{label}</dt>
      <dd className={mono ? "font-mono text-ink-2" : "font-mono text-ink-2"}>{value}</dd>
    </div>
  );
}

function formatMilli(milli: number): string {
  const whole = Math.trunc(milli / 1000);
  const remainder = milli % 1000;
  if (remainder === 0) return whole.toLocaleString("en-IN");
  return `${whole.toLocaleString("en-IN")}.${String(remainder).padStart(3, "0").replace(/0+$/, "")}`;
}

function initials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  return ((words[0]?.[0] ?? "") + (words[1]?.[0] ?? words[0]?.[1] ?? "")).toUpperCase();
}
